import { trackClarityEvent } from './clarity'

declare global {
  interface Window {
    // Undefined until GTM is initialized (or its snippet ran).
    dataLayer?: unknown[]
  }
}

const GTM_ID = import.meta.env.VITE_GTM_ID
const GTM_ENABLED = import.meta.env.VITE_GTM_ENABLED === 'true'
export const GTM_DEBUG = import.meta.env.VITE_GTM_DEBUG === 'true'

let isInitialized = false

/** Opt-in console output for checking events locally (VITE_GTM_DEBUG). */
function debugLog(...args: unknown[]): void {
  if (!GTM_DEBUG) return
  // eslint-disable-next-line no-console -- deliberate opt-in debug output, enabled only by VITE_GTM_DEBUG
  console.log(...args)
}

export function initializeGTM(): void {
  if (isInitialized) {
    return
  }

  debugLog('[GTM] Debug mode enabled')
  debugLog('[GTM] GTM_ENABLED:', GTM_ENABLED)
  debugLog('[GTM] GTM_ID:', GTM_ID || 'not set')

  if (!GTM_ID) {
    if (GTM_DEBUG) {
      console.warn('[GTM] Missing VITE_GTM_ID environment variable - tracking disabled')
    }
    isInitialized = true
    return
  }

  if (!GTM_ENABLED) {
    debugLog('[GTM] Tracking disabled (VITE_GTM_ENABLED is not true)')
    debugLog('[GTM] Events will be logged to console only')
    isInitialized = true
    return
  }

  // Initialize dataLayer
  window.dataLayer ??= []
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  })

  // Load the GTM script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  document.head.appendChild(script)

  // Add noscript iframe for GTM
  const noscript = document.createElement('noscript')
  const iframe = document.createElement('iframe')
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`
  iframe.height = '0'
  iframe.width = '0'
  iframe.style.display = 'none'
  iframe.style.visibility = 'hidden'
  noscript.appendChild(iframe)
  document.body.insertBefore(noscript, document.body.firstChild)

  isInitialized = true
  debugLog('[GTM] Initialized and tracking to container:', GTM_ID)
}

/**
 * Low-level dataLayer push. Also mirrors the event into Clarity so session
 * recordings are filterable by the same funnel events. Domain tracking belongs
 * in `lib/track` — do not call this from components.
 */
export function pushToDataLayer(data: Record<string, unknown>): void {
  // Always log in debug mode, regardless of GTM_ENABLED
  debugLog('[GTM Event]', data.event, data)

  // Mirror every funnel event into Clarity. Independent of GTM (Clarity has its
  // own production gate and no-ops otherwise). Skip the internal GTM bootstrap
  // event and virtual pageviews — Clarity tracks navigation natively.
  const eventName = typeof data.event === 'string' ? data.event : undefined
  if (eventName && eventName !== 'gtm.js' && eventName !== 'virtual_pageview') {
    trackClarityEvent(eventName)
  }

  // Only push to GTM if enabled
  if (!GTM_ENABLED) return

  if (!isInitialized) {
    if (GTM_DEBUG) {
      console.warn('[GTM] Event ignored - GTM not initialized')
    }
    return
  }

  window.dataLayer?.push(data)
}
